// Admin-only tool: batch-generate, for every tile in a project's GPKG tile index, (a) an
// individual Potree project (3D) and (b) an elevation COG for the project's 2D mosaic
// (docs/2d-mosaic-tileset-spec.md — colorized at render time by TiTiler with a mosaic-wide
// stretch). Admin-gated via src/tools/index.js's GROUP_TOOL_ACCESS (this id is never
// added to a non-admin group's array, so only the Cognito "admin" group ever sees it — no
// separate access-control code needed here).
//
// This is deliberately its own tool rather than a button inside Point Cloud Workspace: regular
// users only ever see tiles as already-generated (or converting) via that tool's "View in 3D"
// popup button; triggering potentially hundreds of multi-minute conversions is an admin
// operation with its own picker, progress view, and throttling concerns.

const STATE_KEY = "hydrotools:potree-bulk-generator:v1";
const JOB_BURST = 3; // fire this many job-creation requests immediately
const JOB_GAP_MS = 13000; // then space the rest out — nginx caps /potree/jobs at 5 req/min
const POLL_INTERVAL_MS = 4000;

export const potreeBulkGeneratorTool = {
  id: "potree-bulk-generator",
  name: "Potree Bulk Generator",
  init(app) { this._rt = this._rt || createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot() || null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore(data); },
};

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

function normalizeTileJobEntry(e) {
  return {
    jobId: String(e?.jobId || ""),
    status: String(e?.status || "queued"),
    viewerUrl: String(e?.viewerUrl || ""),
    updatedAt: Number(e?.updatedAt || 0),
  };
}

// Lifecycle detail (stage/progress/message) lives on the in-memory entry only — it changes
// every poll tick and is meaningless after the job ends, so persistTileIndexSidecar strips it
// (via normalizeTileJobEntry) rather than churning the S3 sidecar with it.
function withLiveProgress(entry, job) {
  entry.stage = String(job?.stage || "");
  entry.progressPct = Math.max(0, Math.min(100, Number(job?.progressPct) || 0));
  entry.message = String(job?.message || "");
  return entry;
}

const STAGE_LABELS = {
  queued: "Queued",
  prepare: "Preparing",
  preflight: "Disk check",
  downloading: "Downloading",
  scan: "Reading metadata",
  convert: "Converting",
  stats: "Computing stats",
  reproject: "Reprojecting",
  colorize: "Colorizing",
  tiling: "Tiling",
  cog: "Writing COG",
  postprocess: "Post-processing",
  upload: "Uploading",
  manifest: "Registering",
  cleanup: "Cleaning up",
  done: "Done",
  failed: "Failed",
};

// Migrates the old { tileIndexKey: { tileName: entry } } shape (values one level too deep,
// detected by the value not itself looking like an entry) into the new flat, project-wide
// { tileName: entry } shape — see point-cloud-workspace/index.js's identical helper for why:
// a Potree conversion belongs to the LAZ file, not to whichever tile-index GPKG referenced it
// when the job ran, so this keeps already-converted tiles recognized across a regenerate.
function flattenTileIndexSidecar(raw) {
  const flat = {};
  for (const [key, value] of Object.entries(raw || {})) {
    if (!value || typeof value !== "object") continue;
    if ("status" in value || "jobId" in value) {
      flat[key] = normalizeTileJobEntry(value);
    } else {
      for (const [tileName, entry] of Object.entries(value)) {
        if (entry && typeof entry === "object") flat[tileName] = normalizeTileJobEntry(entry);
      }
    }
  }
  return flat;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createRuntime(app) {
  let containerEl = null;
  let state = readState();

  let clients = [];
  let projects = []; // all projects, every client — filtered per-client for the picker via projectsForActiveClient()
  let tileIndexFiles = []; // [{key, name}] for the selected project's vector-files GPKGs
  let tileIndexSidecar = {}; // Potree sidecar for the selected project, flat: {tileName: entry}
  let rasterSidecar = {}; // 2D-mosaic COG sidecar (separate S3 file, kind=raster2d)
  let clsSidecar = {}; // classification COG sidecar (separate S3 file, kind=raster2dcls)
  let tiles = []; // [{name, sourcePath, jobEntry, rasterEntry, clsEntry}] for the selected tile index
  let busy = false;
  let status = "";
  let batchRunning = false;
  let batchDone = 0;
  let batchTotal = 0;
  let batch2dRunning = false;
  let batch2dDone = 0;
  let batch2dTotal = 0;
  let batchClsRunning = false;
  let batchClsDone = 0;
  let batchClsTotal = 0;
  let pollTimer = null;
  let mosaicInfo = null; // last-fetched mosaic summary for the active project

  // Mirrors the server's mosaic2dClassFilterKey (services/ais-api/src/server.js) so the
  // client can look up completion state under the same key the server will store it under.
  function currentRaster2dAttrKey() {
    const classes = [...new Set((state.raster2dClasses || []).map(Number).filter(Number.isInteger))].sort((a, b) => a - b);
    return classes.length ? `elevation:${classes.join(",")}` : "elevation";
  }

  function readState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        activeClientId: String(parsed?.activeClientId || ""),
        activeCode: String(parsed?.activeCode || ""),
        selectedTileIndexKey: String(parsed?.selectedTileIndexKey || ""),
        raster2dEpsg: String(parsed?.raster2dEpsg || ""),
        raster2dResolution: String(parsed?.raster2dResolution || "0.1"),
        // Empty = today's default (all classes, unfiltered DEM). Any classes checked here
        // route generate-cog through classFilter, producing an independent DEM product for
        // that exact class set (see resolveMosaic2dAttr on the server) instead of overwriting
        // the default elevation mosaic.
        raster2dClasses: Array.isArray(parsed?.raster2dClasses) ? parsed.raster2dClasses.map(Number).filter(Number.isInteger) : [],
        raster2dLabel: String(parsed?.raster2dLabel || ""),
      };
    } catch {
      return { activeClientId: "", activeCode: "", selectedTileIndexKey: "", raster2dEpsg: "", raster2dResolution: "0.1", raster2dClasses: [], raster2dLabel: "" };
    }
  }

  function persist() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
  }

  async function loadProjects() {
    try {
      await app?.services?.context?.refreshFromServer?.();
    } catch {}
    clients = app?.services?.context?.getClients?.() || [];
    projects = app?.services?.context?.getAllProjects?.() || [];
    render();
  }

  function projectsForActiveClient() {
    if (!state.activeClientId) return [];
    return projects.filter((p) => p.clientId === state.activeClientId);
  }

  function activeProject() {
    return projects.find((p) => p.code === state.activeCode) || null;
  }

  function selectClient(clientId) {
    state.activeClientId = String(clientId || "").trim();
    // Changing the client invalidates whatever project/tile-index was selected under the
    // previous one — clear the whole downstream chain rather than leaving a stale selection.
    state.activeCode = "";
    state.selectedTileIndexKey = "";
    persist();
    tileIndexFiles = [];
    tiles = [];
    stopPolling();
    render();
  }

  async function selectProject(code, { preserveTileIndex = false } = {}) {
    state.activeCode = String(code || "").trim();
    const keepTileIndex = preserveTileIndex ? state.selectedTileIndexKey : "";
    state.selectedTileIndexKey = "";
    persist();
    tileIndexFiles = [];
    tiles = [];
    stopPolling();
    render();
    if (!state.activeCode) return;
    busy = true;
    status = `Loading tile indexes for ${state.activeCode}...`;
    render();
    try {
      const payload = await requestJson(`/api/project/assets?code=${encodeURIComponent(state.activeCode)}&category=vector-files`);
      tileIndexFiles = (Array.isArray(payload?.files) ? payload.files : [])
        .filter((item) => /\.gpkg$/i.test(String(item?.name || item?.key || "")))
        .map((item) => ({ key: String(item?.key || "").trim(), name: String(item?.name || "").trim() }))
        .filter((item) => item.key);
      status = tileIndexFiles.length ? `Found ${tileIndexFiles.length} tile index(es).` : "No tile index (.gpkg) uploaded for this project yet.";
    } catch (err) {
      status = `Failed to load tile indexes: ${String(err?.message || err)}`;
    } finally {
      busy = false;
      render();
    }
    if (keepTileIndex && tileIndexFiles.some((f) => f.key === keepTileIndex)) {
      await selectTileIndex(keepTileIndex);
    }
  }

  async function selectTileIndex(key) {
    state.selectedTileIndexKey = String(key || "").trim();
    persist();
    tiles = [];
    stopPolling();
    render();
    if (!state.selectedTileIndexKey) return;
    await refreshTiles();
  }

  async function fetchTileIndexGeojson(key, layer, assumedSrs) {
    const res = await fetch("/api/project/asset/gpkg-geojson", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: state.activeCode, key, layer: layer || undefined, assumedSrs: assumedSrs || undefined }),
    });
    const payload = await res.json().catch(() => ({}));
    if (res.status === 422 && payload?.needsLayer) {
      const layers = Array.isArray(payload.layers) ? payload.layers : [];
      const choice = window.prompt(`This GeoPackage has multiple layers:\n${layers.join(", ")}\nEnter the layer to use:`, layers[0] || "");
      if (!choice) throw new Error("Layer selection cancelled.");
      return fetchTileIndexGeojson(key, choice.trim(), assumedSrs);
    }
    if (res.status === 422 && payload?.needsSrs) {
      const suggestion = activeProject()?.horizontalCRS || "EPSG:26910";
      const choice = window.prompt(
        "This GeoPackage layer has no coordinate system defined.\nEnter the EPSG code its coordinates are actually in:",
        suggestion,
      );
      if (!choice) throw new Error("Source CRS entry cancelled.");
      return fetchTileIndexGeojson(key, layer, choice.trim());
    }
    if (!res.ok) throw new Error(String(payload?.error || `Request failed (${res.status})`));
    return payload.geojson;
  }

  // Re-derives everything from current server state rather than trusting any in-memory
  // continuation — safe to call after a fresh mount, a page reload mid-batch, or just to
  // manually refresh, and always produces the correct "what's left to generate" picture.
  async function refreshTiles() {
    if (!state.activeCode || !state.selectedTileIndexKey) return;
    busy = true;
    status = "Loading tile list...";
    render();
    try {
      const project = activeProject();
      const [geojson, lasPayload, tileIndexPayload, rasterPayload, clsPayload, mosaicPayload] = await Promise.all([
        fetchTileIndexGeojson(state.selectedTileIndexKey, undefined, project?.horizontalCRS || ""),
        requestJson(`/api/project/assets?code=${encodeURIComponent(state.activeCode)}&category=las-files`),
        requestJson(`/api/point-cloud/potree/tile-index?marina=${encodeURIComponent(state.activeCode)}`),
        requestJson(`/api/point-cloud/potree/tile-index?marina=${encodeURIComponent(state.activeCode)}&kind=raster2d`).catch(() => ({})),
        requestJson(`/api/point-cloud/potree/tile-index?marina=${encodeURIComponent(state.activeCode)}&kind=raster2dcls`).catch(() => ({})),
        requestJson(`/api/point-cloud/mosaic2d/${encodeURIComponent(state.activeCode)}`).catch(() => null),
      ]);
      const lasByName = new Map(
        (Array.isArray(lasPayload?.files) ? lasPayload.files : [])
          .map((f) => [String(f?.name || "").trim().toLowerCase(), String(f?.key || "").trim()])
          .filter(([name, key]) => name && key),
      );
      // Tile indexes name tiles by bare filename, but las-files may hold them in a subfolder
      // (e.g. NOAA imports land in las-files/noaa-<id>/). Fall back to the basename when it's
      // unique across the project; an ambiguous basename stays unmatched rather than guessed.
      const lasByBase = new Map();
      for (const [name, key] of lasByName) {
        const base = name.split("/").pop();
        lasByBase.set(base, lasByBase.has(base) ? null : key);
      }
      const lasKeyFor = (name) => lasByName.get(name.toLowerCase()) || lasByBase.get(name.toLowerCase()) || "";
      tileIndexSidecar = flattenTileIndexSidecar(tileIndexPayload?.tileIndex);
      rasterSidecar = flattenTileIndexSidecar(rasterPayload?.tileIndex);
      clsSidecar = flattenTileIndexSidecar(clsPayload?.tileIndex);
      mosaicInfo = mosaicPayload?.ok ? mosaicPayload.mosaic : null;
      // The mosaic manifest is the durable truth for 2D completion — a tile registered there
      // is done even if the sidecar lost track of it (e.g. saved from another session).
      const mosaicTiles = mosaicPayload?.ok && mosaicPayload.tiles ? mosaicPayload.tiles : {};
      const features = Array.isArray(geojson?.features) ? geojson.features : [];
      const seen = new Set();
      // Whichever class-filtered DEM is currently selected (or plain "elevation" if none) —
      // the 2D Mosaic section below tracks completion against THIS key, so checking classes
      // transparently repoints Generate 2D / the progress counts / the table column at that
      // product instead of duplicating the whole section per possible class combination.
      const raster2dAttrKey = currentRaster2dAttrKey();
      tiles = features
        .map((f) => String(f?.properties?.name || f?.properties?.filename || "").trim())
        .filter((name) => {
          if (!name || seen.has(name.toLowerCase())) return false;
          seen.add(name.toLowerCase());
          return true;
        })
        .map((name) => ({
          name,
          sourcePath: lasKeyFor(name),
          // Keyed by tile name alone, project-wide — a tile already converted under a previous
          // (now regenerated/replaced) tile index still shows as completed here.
          jobEntry: tileIndexSidecar[name] ? normalizeTileJobEntry(tileIndexSidecar[name]) : null,
          rasterEntry: (() => {
            const sc = rasterSidecar[name] ? normalizeTileJobEntry(rasterSidecar[name]) : null;
            if (sc?.jobId && !["completed", "failed", "skipped"].includes(sc.status)) return sc; // live job wins
            if (mosaicTiles[name]?.attrs ? mosaicTiles[name].attrs[raster2dAttrKey]?.cogKey : (raster2dAttrKey === "elevation" && mosaicTiles[name])) {
              return normalizeTileJobEntry({ ...(sc || {}), status: "completed" });
            }
            // The sidecar is one job entry per tile name, not per product — a "completed" entry
            // left over from generating a DIFFERENT product (e.g. the unfiltered elevation DEM)
            // can't stand in for this attrKey just because the manifest doesn't have it yet, or
            // every tile would falsely show as already done the first time a class filter is
            // picked. Only a genuine failure or empty-result is still worth surfacing across
            // products (and gets overwritten the moment this tile is actually (re)submitted).
            return (sc?.status === "failed" || sc?.status === "skipped") ? sc : null;
          })(),
          clsEntry: (() => {
            const sc = clsSidecar[name] ? normalizeTileJobEntry(clsSidecar[name]) : null;
            if (sc?.jobId && !["completed", "failed", "skipped"].includes(sc.status)) return sc; // live job wins
            if (mosaicTiles[name]?.attrs?.classification?.cogKey) {
              return normalizeTileJobEntry({ ...(sc || {}), status: "completed" });
            }
            return sc;
          })(),
        }));
      status = `${tiles.length} tile(s) in index — ${tiles.filter((t) => t.sourcePath).length} uploaded, ${tiles.filter((t) => t.jobEntry?.status === "completed").length} converted (3D), ${tiles.filter((t) => t.rasterEntry?.status === "completed").length} in 2D mosaic, ${tiles.filter((t) => t.clsEntry?.status === "completed").length} classified.`;
      const hasActive = tiles.some((t) =>
        (t.jobEntry?.jobId && !["completed", "failed", "skipped"].includes(t.jobEntry.status)) ||
        (t.rasterEntry?.jobId && !["completed", "failed", "skipped"].includes(t.rasterEntry.status)) ||
        (t.clsEntry?.jobId && !["completed", "failed", "skipped"].includes(t.clsEntry.status)));
      if (hasActive) startPolling();
      if (!state.raster2dEpsg && project?.horizontalCRS && /^EPSG:\d+$/i.test(project.horizontalCRS)) {
        state.raster2dEpsg = project.horizontalCRS.toUpperCase();
        persist();
      }
    } catch (err) {
      status = `Failed to load tiles: ${String(err?.message || err)}`;
      tiles = [];
    } finally {
      busy = false;
      render();
    }
  }

  let persistTimer = null;
  function persistTileIndexSidecar() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(async () => {
      try {
        // Merge into the already-loaded flat sidecar rather than replace it — it may contain
        // entries for tiles outside today's selected index (e.g. tracked under a tile index
        // that's since been regenerated or removed) that shouldn't be dropped.
        for (const t of tiles) {
          if (t.jobEntry) tileIndexSidecar[t.name] = normalizeTileJobEntry(t.jobEntry);
        }
        await requestJson("/api/point-cloud/potree/tile-index", {
          method: "POST",
          body: { marina: state.activeCode, tileIndex: tileIndexSidecar },
        });
      } catch (err) {
        console.warn("[potree-bulk-generator] failed to persist tile index:", err);
      }
    }, 1500);
  }

  let persistRasterTimer = null;
  function persistRasterSidecar() {
    clearTimeout(persistRasterTimer);
    persistRasterTimer = setTimeout(async () => {
      try {
        for (const t of tiles) {
          if (t.rasterEntry) rasterSidecar[t.name] = normalizeTileJobEntry(t.rasterEntry);
        }
        await requestJson("/api/point-cloud/potree/tile-index", {
          method: "POST",
          body: { marina: state.activeCode, kind: "raster2d", tileIndex: rasterSidecar },
        });
      } catch (err) {
        console.warn("[potree-bulk-generator] failed to persist raster2d sidecar:", err);
      }
    }, 1500);
  }

  let persistClsTimer = null;
  function persistClsSidecar() {
    clearTimeout(persistClsTimer);
    persistClsTimer = setTimeout(async () => {
      try {
        for (const t of tiles) {
          if (t.clsEntry) clsSidecar[t.name] = normalizeTileJobEntry(t.clsEntry);
        }
        await requestJson("/api/point-cloud/potree/tile-index", {
          method: "POST",
          body: { marina: state.activeCode, kind: "raster2dcls", tileIndex: clsSidecar },
        });
      } catch (err) {
        console.warn("[potree-bulk-generator] failed to persist raster2dcls sidecar:", err);
      }
    }, 1500);
  }

  async function submitClsJobForTile(tile) {
    try {
      const res = await requestJson("/api/point-cloud/generate-cog", {
        method: "POST",
        body: {
          sourcePath: tile.sourcePath,
          sourceEpsg: state.raster2dEpsg,
          resolution: Number(state.raster2dResolution) || 0.1,
          marina: state.activeCode,
          datasetName: tile.name,
          attribute: "classification",
        },
      });
      tile.clsEntry = withLiveProgress(normalizeTileJobEntry({
        jobId: res?.jobId || "",
        status: "queued",
        updatedAt: Date.now(),
      }), null);
    } catch (err) {
      tile.clsEntry = normalizeTileJobEntry({ status: "failed", updatedAt: Date.now() });
      tile.clsEntry.message = String(err?.message || err || "Job submission failed.");
      console.warn(`[potree-bulk-generator] classification job submission failed for ${tile.name}:`, err);
    }
    batchClsDone += 1;
    persistClsSidecar();
    render();
  }

  async function startBatchClsGeneration() {
    if (batchClsRunning || !state.activeCode || !state.selectedTileIndexKey) return;
    if (!/^EPSG:\d+$/i.test(String(state.raster2dEpsg || "").trim())) {
      status = "Enter the source EPSG for 2D generation first (e.g. EPSG:26915).";
      render();
      return;
    }
    const todo = tiles.filter((t) => t.sourcePath && (!t.clsEntry || t.clsEntry.status === "failed"));
    if (!todo.length) {
      status = "Nothing to generate — every uploaded tile already has (or is getting) a classification COG.";
      render();
      return;
    }
    batchClsRunning = true;
    batchClsDone = 0;
    batchClsTotal = todo.length;
    status = `Starting classification batch: ${todo.length} tile(s) to rasterize...`;
    render();
    startPolling();
    try { app.log?.("cog2dcls.bulk_job_started", { count: todo.length, marina: state.activeCode }); } catch {}
    for (let i = 0; i < todo.length; i++) {
      if (!batchClsRunning) break; // Stop button
      if (i >= JOB_BURST) await sleep(JOB_GAP_MS);
      await submitClsJobForTile(todo[i]);
    }
    batchClsRunning = false;
    status = "Classification batch submission complete — polling for rasterization progress.";
    render();
    startPolling();
  }

  function stopBatchClsGeneration() {
    if (!batchClsRunning) return;
    batchClsRunning = false;
    status = "Classification batch stopped — already-submitted jobs will keep running.";
    render();
  }

  async function submitCogJobForTile(tile) {
    try {
      const res = await requestJson("/api/point-cloud/generate-cog", {
        method: "POST",
        body: {
          sourcePath: tile.sourcePath,
          sourceEpsg: state.raster2dEpsg,
          resolution: Number(state.raster2dResolution) || 0.1,
          marina: state.activeCode,
          datasetName: tile.name,
          ...(state.raster2dClasses?.length ? { classFilter: state.raster2dClasses, label: state.raster2dLabel } : {}),
        },
      });
      tile.rasterEntry = withLiveProgress(normalizeTileJobEntry({
        jobId: res?.jobId || "",
        status: "queued",
        updatedAt: Date.now(),
      }), null);
    } catch (err) {
      tile.rasterEntry = normalizeTileJobEntry({ status: "failed", updatedAt: Date.now() });
      tile.rasterEntry.message = String(err?.message || err || "Job submission failed.");
      console.warn(`[potree-bulk-generator] 2D job submission failed for ${tile.name}:`, err);
    }
    batch2dDone += 1;
    persistRasterSidecar();
    render();
  }

  async function startBatch2dGeneration() {
    if (batch2dRunning || !state.activeCode || !state.selectedTileIndexKey) return;
    if (!/^EPSG:\d+$/i.test(String(state.raster2dEpsg || "").trim())) {
      status = "Enter the source EPSG for 2D generation first (e.g. EPSG:26915).";
      render();
      return;
    }
    const todo = tiles.filter((t) => t.sourcePath && (!t.rasterEntry || t.rasterEntry.status === "failed"));
    if (!todo.length) {
      status = "Nothing to generate — every uploaded tile is already in (or headed into) the 2D mosaic.";
      render();
      return;
    }
    batch2dRunning = true;
    batch2dDone = 0;
    batch2dTotal = todo.length;
    status = `Starting 2D batch: ${todo.length} tile(s) to rasterize...`;
    render();
    startPolling(); // live-update already-submitted tiles during the (long) submission loop
    try { app.log?.("cog2d.bulk_job_started", { count: todo.length, marina: state.activeCode }); } catch {}
    for (let i = 0; i < todo.length; i++) {
      if (!batch2dRunning) break; // Stop button
      if (i >= JOB_BURST) await sleep(JOB_GAP_MS);
      await submitCogJobForTile(todo[i]);
    }
    batch2dRunning = false;
    status = "2D batch submission complete — polling for rasterization progress.";
    render();
    startPolling();
  }

  function stopBatch2dGeneration() {
    if (!batch2dRunning) return;
    batch2dRunning = false;
    status = "2D batch stopped — already-submitted jobs will keep running.";
    render();
  }

  async function submitJobForTile(tile) {
    try {
      const res = await requestJson("/api/point-cloud/potree/jobs", {
        method: "POST",
        body: { sourcePath: tile.sourcePath, marina: state.activeCode, datasetName: tile.name },
      });
      tile.jobEntry = withLiveProgress(normalizeTileJobEntry({
        jobId: res?.job?.id || "",
        status: res?.job?.status || "queued",
        viewerUrl: res?.job?.viewerUrl || "",
        updatedAt: Date.now(),
      }), res?.job);
    } catch (err) {
      tile.jobEntry = normalizeTileJobEntry({ status: "failed", updatedAt: Date.now() });
      tile.jobEntry.message = String(err?.message || err || "Job submission failed.");
      console.warn(`[potree-bulk-generator] job submission failed for ${tile.name}:`, err);
    }
    batchDone += 1;
    persistTileIndexSidecar();
    render();
  }

  async function startBatchGeneration() {
    if (batchRunning || !state.activeCode || !state.selectedTileIndexKey) return;
    // Always recompute from current state — resumable across reloads, never assumes a
    // continuous session. Tiles that previously failed are retried; anything queued/running/
    // completed is left alone.
    const todo = tiles.filter((t) => t.sourcePath && (!t.jobEntry || t.jobEntry.status === "failed"));
    if (!todo.length) {
      status = "Nothing to generate — every uploaded tile already has a job.";
      render();
      return;
    }
    batchRunning = true;
    batchDone = 0;
    batchTotal = todo.length;
    status = `Starting batch: ${todo.length} tile(s) to convert...`;
    render();
    startPolling(); // live-update already-submitted tiles during the (long) submission loop
    try { app.log?.("potree.bulk_job_started", { count: todo.length, marina: state.activeCode }); } catch {}
    for (let i = 0; i < todo.length; i++) {
      if (!batchRunning) break; // Stop button
      if (i >= JOB_BURST) await sleep(JOB_GAP_MS);
      await submitJobForTile(todo[i]);
    }
    batchRunning = false;
    status = "Batch submission complete — polling for conversion progress.";
    render();
    startPolling();
  }

  function stopBatchGeneration() {
    if (!batchRunning) return;
    batchRunning = false;
    status = "Batch stopped — already-submitted jobs will keep converting.";
    render();
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(async () => {
      const activePotree = tiles.filter((t) => t.jobEntry?.jobId && !["completed", "failed", "skipped"].includes(t.jobEntry.status));
      const activeRaster = tiles.filter((t) => t.rasterEntry?.jobId && !["completed", "failed", "skipped"].includes(t.rasterEntry.status));
      const activeCls = tiles.filter((t) => t.clsEntry?.jobId && !["completed", "failed", "skipped"].includes(t.clsEntry.status));
      // Keep polling while a batch is still submitting even if nothing is active yet —
      // otherwise the table freezes at "queued 0%" for the whole (hours-long) submission loop.
      if (!activePotree.length && !activeRaster.length && !activeCls.length) {
        if (!batchRunning && !batch2dRunning && !batchClsRunning) stopPolling();
        return;
      }
      // Jobs live in server memory only until completed — a deploy/crash restart wipes the
      // queue, leaving entries "queued" forever and invisible to batch retry (which only
      // picks up "failed"). An explicit job_not_found means the job is gone; mark it failed
      // so the next Generate resubmits it. The age guard skips entries the server may still
      // be rehydrating from S3 right after a restart.
      const orphaned = (entry, err) =>
        String(err?.message) === "job_not_found" && Date.now() - (entry.updatedAt || 0) > 120_000;
      await Promise.all([
        ...activePotree.map(async (t) => {
          try {
            const res = await requestJson(`/api/point-cloud/potree/jobs/${encodeURIComponent(t.jobEntry.jobId)}`);
            const job = res?.job;
            if (!job) return;
            t.jobEntry = withLiveProgress(normalizeTileJobEntry({
              ...t.jobEntry,
              status: job.status || t.jobEntry.status,
              viewerUrl: job.viewerUrl || t.jobEntry.viewerUrl,
              updatedAt: Date.now(),
            }), job);
          } catch (err) {
            if (orphaned(t.jobEntry, err)) {
              t.jobEntry = normalizeTileJobEntry({ ...t.jobEntry, status: "failed", updatedAt: Date.now() });
              t.jobEntry.message = "Job lost to a server restart — Generate All will resubmit it.";
            }
          }
        }),
        ...activeRaster.map(async (t) => {
          try {
            const res = await requestJson(`/api/point-cloud/generate-tiles/jobs/${encodeURIComponent(t.rasterEntry.jobId)}`);
            const job = res?.job;
            if (!job) return;
            t.rasterEntry = withLiveProgress(normalizeTileJobEntry({
              ...t.rasterEntry,
              status: job.status || t.rasterEntry.status,
              updatedAt: Date.now(),
            }), job);
          } catch (err) {
            if (orphaned(t.rasterEntry, err)) {
              t.rasterEntry = normalizeTileJobEntry({ ...t.rasterEntry, status: "failed", updatedAt: Date.now() });
              t.rasterEntry.message = "Job lost to a server restart — Generate 2D will resubmit it.";
            }
          }
        }),
        ...activeCls.map(async (t) => {
          try {
            const res = await requestJson(`/api/point-cloud/generate-tiles/jobs/${encodeURIComponent(t.clsEntry.jobId)}`);
            const job = res?.job;
            if (!job) return;
            t.clsEntry = withLiveProgress(normalizeTileJobEntry({
              ...t.clsEntry,
              status: job.status || t.clsEntry.status,
              updatedAt: Date.now(),
            }), job);
          } catch (err) {
            if (orphaned(t.clsEntry, err)) {
              t.clsEntry = normalizeTileJobEntry({ ...t.clsEntry, status: "failed", updatedAt: Date.now() });
              t.clsEntry.message = "Job lost to a server restart — Generate Classification will resubmit it.";
            }
          }
        }),
      ]);
      persistTileIndexSidecar();
      persistRasterSidecar();
      persistClsSidecar();
      render();
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  }

  // One Leaflet layer for the whole mosaic. The tile URL carries the mosaic-wide stretch
  // (rescale) and the manifest version (v) — re-adding the layer after new tiles land picks
  // up both fresh values, so colors always follow the mosaic's current min/max.
  async function showMosaicOnMap() {
    const layersService = app?.services?.layers;
    if (!layersService || !state.activeCode) return;
    status = "Loading 2D mosaic info...";
    render();
    try {
      const payload = await requestJson(`/api/point-cloud/mosaic2d/${encodeURIComponent(state.activeCode)}?fresh=1`);
      const mosaic = payload?.mosaic;
      mosaicInfo = mosaic || null;
      if (!mosaic?.tileCount) {
        status = "No tiles in the 2D mosaic yet — run Generate 2D first.";
        render();
        return;
      }
      // Attr-aware removal: elevation, classification, and any class-filtered DEM are separate
      // map layers that may coexist — only replace the one being re-shown (pre-attr layers have
      // no meta.attr and count as plain elevation).
      const attrKey = currentRaster2dAttrKey();
      for (const l of layersService.listLayers().filter((l) =>
        l.meta?.mosaic2dCode === state.activeCode && (l.meta?.attr || "elevation") === attrKey)) {
        layersService.removeLayer(l.id);
      }
      const attrStats = mosaic.attrs?.[attrKey];
      const rescale = `${attrStats?.stretchMin ?? 0},${attrStats?.stretchMax ?? 100}`;
      const b = mosaic.bounds;
      const layerName = state.raster2dClasses.length
        ? `2D Mosaic: ${state.activeCode} (${state.raster2dLabel || `Class ${state.raster2dClasses.join(", ")}`})`
        : `2D Mosaic: ${state.activeCode}`;
      layersService.addLayer("potree-bulk-generator", {
        type: "tile-url",
        kind: "overlay",
        name: layerName,
        url: `/api/point-cloud/mosaic2d/${encodeURIComponent(state.activeCode)}/tiles/{z}/{x}/{y}.png?attr=${encodeURIComponent(attrKey)}&rescale=${encodeURIComponent(rescale)}&colormap_name=viridis&v=${encodeURIComponent(mosaic.updatedAt || "")}`,
        opacity: 0.92,
        visible: true,
        meta: { sourceType: "potree-bulk-generator", rasterKind: "mosaic2d", mosaic2dCode: state.activeCode, attr: attrKey },
        options: {
          bounds: b ? { west: b.west, south: b.south, east: b.east, north: b.north } : null,
          minNativeZoom: mosaic.minzoom || 12,
          maxNativeZoom: mosaic.maxzoom || 22,
          maxZoom: 26,
          autoZoom: true,
        },
      });
      status = `2D mosaic on map — ${mosaic.tileCount} tile(s), stretch ${rescale} m.`;
    } catch (err) {
      status = `Failed to load 2D mosaic: ${String(err?.message || err)}`;
    }
    render();
  }

  // Classification layer: same mosaic plumbing, but the server picks the discrete ASPRS
  // colormap (restricted to classes present) and forces nearest resampling — no styling
  // params to carry here.
  async function showClsMosaicOnMap() {
    const layersService = app?.services?.layers;
    if (!layersService || !state.activeCode) return;
    status = "Loading classification mosaic info...";
    render();
    try {
      const payload = await requestJson(`/api/point-cloud/mosaic2d/${encodeURIComponent(state.activeCode)}?fresh=1`);
      const mosaic = payload?.mosaic;
      mosaicInfo = mosaic || null;
      const cls = mosaic?.attrs?.classification;
      if (!cls?.tileCount) {
        status = "No classification tiles yet — run Generate Classification first.";
        render();
        return;
      }
      for (const l of layersService.listLayers().filter((l) =>
        l.meta?.mosaic2dCode === state.activeCode && l.meta?.attr === "classification")) {
        layersService.removeLayer(l.id);
      }
      const b = mosaic.bounds;
      layersService.addLayer("potree-bulk-generator", {
        type: "tile-url",
        kind: "overlay",
        name: `Classification: ${state.activeCode}`,
        url: `/api/point-cloud/mosaic2d/${encodeURIComponent(state.activeCode)}/tiles/{z}/{x}/{y}.png?attr=classification&v=${encodeURIComponent(mosaic.updatedAt || "")}`,
        opacity: 0.92,
        visible: true,
        meta: { sourceType: "potree-bulk-generator", rasterKind: "mosaic2d", mosaic2dCode: state.activeCode, attr: "classification" },
        options: {
          bounds: b ? { west: b.west, south: b.south, east: b.east, north: b.north } : null,
          minNativeZoom: mosaic.minzoom || 12,
          maxNativeZoom: mosaic.maxzoom || 22,
          maxZoom: 26,
          autoZoom: true,
        },
      });
      status = `Classification mosaic on map — ${cls.tileCount} tile(s), classes [${(cls.classes || []).join(", ")}].`;
    } catch (err) {
      status = `Failed to load classification mosaic: ${String(err?.message || err)}`;
    }
    render();
  }

  function onChange(event) {
    const clientSel = event.target?.closest?.("#pbg-client");
    if (clientSel) { selectClient(clientSel.value); return; }
    const projectSel = event.target?.closest?.("#pbg-project");
    if (projectSel) { selectProject(projectSel.value); return; }
    const tileIndexSel = event.target?.closest?.("#pbg-tile-index");
    if (tileIndexSel) { selectTileIndex(tileIndexSel.value); return; }
    const epsgInput = event.target?.closest?.("#pbg-2d-epsg");
    if (epsgInput) {
      state.raster2dEpsg = String(epsgInput.value || "").trim().toUpperCase();
      persist();
      return;
    }
    const resInput = event.target?.closest?.("#pbg-2d-res");
    if (resInput) {
      state.raster2dResolution = String(resInput.value || "").trim();
      persist();
      return;
    }
    const classCheckbox = event.target?.closest?.("[data-2d-class]");
    if (classCheckbox) {
      const code = Number(classCheckbox.getAttribute("data-2d-class"));
      const set = new Set(state.raster2dClasses);
      if (classCheckbox.checked) set.add(code); else set.delete(code);
      state.raster2dClasses = [...set].sort((a, b) => a - b);
      persist();
      render();
      return;
    }
    const labelInput = event.target?.closest?.("#pbg-2d-label");
    if (labelInput) {
      state.raster2dLabel = String(labelInput.value || "").trim();
      persist();
      return;
    }
  }

  function onClick(event) {
    if (event.target?.closest?.("#pbg-refresh")) { refreshTiles(); return; }
    if (event.target?.closest?.("#pbg-start")) { startBatchGeneration(); return; }
    if (event.target?.closest?.("#pbg-stop")) { stopBatchGeneration(); return; }
    if (event.target?.closest?.("#pbg-start-2d")) { startBatch2dGeneration(); return; }
    if (event.target?.closest?.("#pbg-stop-2d")) { stopBatch2dGeneration(); return; }
    if (event.target?.closest?.("#pbg-start-cls")) { startBatchClsGeneration(); return; }
    if (event.target?.closest?.("#pbg-stop-cls")) { stopBatchClsGeneration(); return; }
    if (event.target?.closest?.("#pbg-show-mosaic")) { showMosaicOnMap(); return; }
    if (event.target?.closest?.("#pbg-show-cls-mosaic")) { showClsMosaicOnMap(); return; }
  }

  const STATUS_COLORS = { completed: "#16a34a", running: "#0ea5e9", queued: "#f59e0b", failed: "#ef4444" };

  // One cell per tile showing where the job is in its lifecycle: a slim progress bar driven by
  // the server's overall progressPct, labeled with the current stage. Stage/message only exist
  // while this session is polling; entries rehydrated from the sidecar fall back to status alone.
  function statusCell(jobEntry, hasSource) {
    if (!hasSource) return `<span style="color:#64748b;">Not uploaded</span>`;
    if (!jobEntry?.status) return `<span style="color:#64748b;">Pending generation</span>`;
    const s = jobEntry.status;
    const color = STATUS_COLORS[s] || "#94a3b8";
    if (s === "completed") return `<span style="color:${color};">completed</span>`;
    if (s === "skipped") {
      // Not a failure — a classFilter tile with none of the requested classes did its job
      // correctly; there was just nothing to rasterize. No retry offered, no red.
      const detail = jobEntry.message ? `<div style="color:#94a3b8; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:220px;" title="${esc(jobEntry.message)}">${esc(jobEntry.message)}</div>` : "";
      return `<span style="color:#64748b;">skipped</span>${detail}`;
    }
    if (s === "failed") {
      const detail = jobEntry.message ? `<div style="color:#94a3b8; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px;" title="${esc(jobEntry.message)}">${esc(jobEntry.message)}</div>` : "";
      return `<span style="color:${color};">failed</span>${detail}`;
    }
    // queued / running: lifecycle bar
    const pct = jobEntry.progressPct || 0;
    const label = STAGE_LABELS[jobEntry.stage] || (s === "queued" ? "Queued" : "Working");
    const tooltip = jobEntry.message ? ` title="${esc(jobEntry.message)}"` : "";
    return `
      <div${tooltip}>
        <div style="display:flex; justify-content:space-between; gap:6px; color:${color};">
          <span>${esc(label)}</span><span>${pct}%</span>
        </div>
        <div style="margin-top:2px; height:4px; border-radius:2px; background:rgba(148,163,184,.2); overflow:hidden;">
          <div style="height:100%; width:${pct}%; border-radius:2px; background:${color}; transition:width .3s;"></div>
        </div>
      </div>`;
  }

  function render() {
    if (!containerEl) return;
    const project = activeProject();
    const uploadedCount = tiles.filter((t) => t.sourcePath).length;
    const completedCount = tiles.filter((t) => t.jobEntry?.status === "completed").length;
    const queuedCount = tiles.filter((t) => t.jobEntry?.jobId && t.jobEntry.status === "queued").length;
    const runningCount = tiles.filter((t) => t.jobEntry?.jobId && !["completed", "failed", "queued"].includes(t.jobEntry.status)).length;
    const failedCount = tiles.filter((t) => t.jobEntry?.status === "failed").length;
    const todoCount = tiles.filter((t) => t.sourcePath && (!t.jobEntry || t.jobEntry.status === "failed")).length;
    const raster2dDone = tiles.filter((t) => t.rasterEntry?.status === "completed").length;
    const raster2dActive = tiles.filter((t) => t.rasterEntry?.jobId && !["completed", "failed", "skipped"].includes(t.rasterEntry.status)).length;
    const raster2dFailed = tiles.filter((t) => t.rasterEntry?.status === "failed").length;
    const raster2dTodo = tiles.filter((t) => t.sourcePath && (!t.rasterEntry || t.rasterEntry.status === "failed")).length;
    const raster2dAttrKey = currentRaster2dAttrKey();
    const raster2dAttrStats = mosaicInfo?.attrs?.[raster2dAttrKey] || null;
    const clsDone = tiles.filter((t) => t.clsEntry?.status === "completed").length;
    const clsActive = tiles.filter((t) => t.clsEntry?.jobId && !["completed", "failed", "skipped"].includes(t.clsEntry.status)).length;
    const clsFailed = tiles.filter((t) => t.clsEntry?.status === "failed").length;
    const clsTodo = tiles.filter((t) => t.sourcePath && (!t.clsEntry || t.clsEntry.status === "failed")).length;

    // render() replaces the whole subtree wholesale on every poll tick (every 4s while a batch
    // is running), which would otherwise reset the tile-list scroll position to the top each
    // time — save it here and restore it after the swap below.
    const prevScrollTop = containerEl.querySelector("#pbg-tile-list")?.scrollTop ?? 0;

    containerEl.innerHTML = `
      <div class="toolset-group" open>
        <div class="toolset-content">
          <div class="small" style="font-weight:600;">Potree Bulk Generator</div>
          <div class="small" style="margin-top:4px; opacity:.75;">Admin tool — batch-generate an individual Potree project for every tile in a project's tile index.</div>

          <div style="margin-top:10px;">
            <label class="small" style="display:block; margin-bottom:4px;">Client</label>
            <select id="pbg-client" style="width:100%;"${busy ? " disabled" : ""}>
              <option value="">Select a client…</option>
              ${clients.map((c) => `<option value="${esc(c.id)}"${c.id === state.activeClientId ? " selected" : ""}>${esc(c.name)}</option>`).join("")}
            </select>
          </div>

          ${state.activeClientId ? `
            <div style="margin-top:8px;">
              <label class="small" style="display:block; margin-bottom:4px;">Project</label>
              <select id="pbg-project" style="width:100%;"${busy ? " disabled" : ""}>
                <option value="">Select a project…</option>
                ${projectsForActiveClient().map((p) => `<option value="${esc(p.code)}"${p.code === state.activeCode ? " selected" : ""}>${esc(p.name)} (${esc(p.code)})</option>`).join("")}
              </select>
            </div>
          ` : ""}

          ${state.activeCode ? `
            <div style="margin-top:8px;">
              <label class="small" style="display:block; margin-bottom:4px;">Tile Index</label>
              <select id="pbg-tile-index" style="width:100%;"${busy || !tileIndexFiles.length ? " disabled" : ""}>
                <option value="">${tileIndexFiles.length ? "Select a tile index…" : "No tile index uploaded"}</option>
                ${tileIndexFiles.map((f) => `<option value="${esc(f.key)}"${f.key === state.selectedTileIndexKey ? " selected" : ""}>${esc(f.name)}</option>`).join("")}
              </select>
            </div>
          ` : ""}

          ${state.selectedTileIndexKey ? `
            <div style="margin-top:10px; display:grid; grid-template-columns:repeat(5,1fr); gap:6px; text-align:center;">
              <div class="small" style="background:rgba(148,163,184,.1); border-radius:6px; padding:6px;"><div style="font-weight:700; font-size:14px;">${tiles.length}</div>Tiles</div>
              <div class="small" style="background:rgba(148,163,184,.1); border-radius:6px; padding:6px;"><div style="font-weight:700; font-size:14px;">${uploadedCount}</div>Uploaded</div>
              <div class="small" style="background:rgba(148,163,184,.1); border-radius:6px; padding:6px;"><div style="font-weight:700; font-size:14px; color:#f59e0b;">${queuedCount}</div>Queued</div>
              <div class="small" style="background:rgba(148,163,184,.1); border-radius:6px; padding:6px;"><div style="font-weight:700; font-size:14px; color:#0ea5e9;">${runningCount}</div>Converting</div>
              <div class="small" style="background:rgba(148,163,184,.1); border-radius:6px; padding:6px;"><div style="font-weight:700; font-size:14px; color:#16a34a;">${completedCount}</div>Done</div>
            </div>

            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="pbg-refresh" class="btn"${busy ? " disabled" : ""}>Refresh</button>
              ${batchRunning
                ? `<button id="pbg-stop" class="btn">Stop Batch</button>`
                : `<button id="pbg-start" class="btn primary"${busy || !todoCount ? " disabled" : ""}>Generate All (${todoCount} tile${todoCount === 1 ? "" : "s"})</button>`}
            </div>
            ${batchRunning ? `<div class="small" style="margin-top:6px;">Submitting jobs: ${batchDone}/${batchTotal}</div>` : ""}
            ${failedCount ? `<div class="small" style="margin-top:4px; color:#ef4444;">${failedCount} tile(s) failed — re-running Generate All will retry them.</div>` : ""}

            <div style="margin-top:12px; padding-top:10px; border-top:1px solid rgba(148,163,184,.22);">
              <div class="small" style="font-weight:600;">2D Mosaic (elevation COGs, colorized at view time)</div>
              <div class="small" style="margin-top:4px; opacity:.75;">${raster2dDone}/${uploadedCount} in mosaic${raster2dActive ? ` · ${raster2dActive} active` : ""}${raster2dAttrStats?.stretchMin != null ? ` · stretch ${raster2dAttrStats.stretchMin}–${raster2dAttrStats.stretchMax} m` : ""}</div>
              <div class="row" style="margin-top:6px; gap:6px;">
                <input id="pbg-2d-epsg" type="text" placeholder="EPSG:26915" value="${esc(state.raster2dEpsg)}" style="flex:1; min-width:0;"${batch2dRunning ? " disabled" : ""} />
                <input id="pbg-2d-res" type="number" step="0.05" min="0.05" max="10" title="Raster resolution (m)" value="${esc(state.raster2dResolution)}" style="width:64px;"${batch2dRunning ? " disabled" : ""} />
              </div>
              ${mosaicInfo?.attrs?.classification?.classes?.length ? `
                <div class="small" style="margin-top:8px; opacity:.75;">Restrict to classes (optional — unchecked = all classes, today's default DEM):</div>
                <div class="row" style="margin-top:4px; gap:10px; flex-wrap:wrap;">
                  ${mosaicInfo.attrs.classification.classes.map((c) => `
                    <label class="small" style="display:flex; align-items:center; gap:4px; cursor:pointer;">
                      <input type="checkbox" data-2d-class="${c}"${state.raster2dClasses.includes(c) ? " checked" : ""}${batch2dRunning ? " disabled" : ""} />
                      Class ${c}
                    </label>
                  `).join("")}
                </div>
                ${state.raster2dClasses.length ? `
                  <input id="pbg-2d-label" type="text" placeholder="Label (e.g. Bathy Only)" value="${esc(state.raster2dLabel)}" style="margin-top:6px; width:100%;"${batch2dRunning ? " disabled" : ""} />
                ` : ""}
              ` : ""}
              <div class="row lm-add-row" style="margin-top:6px;">
                ${batch2dRunning
                  ? `<button id="pbg-stop-2d" class="btn">Stop 2D Batch</button>`
                  : `<button id="pbg-start-2d" class="btn primary"${busy || !raster2dTodo ? " disabled" : ""}>${state.raster2dClasses.length ? `Generate Class DEM` : "Generate 2D"} (${raster2dTodo} tile${raster2dTodo === 1 ? "" : "s"})</button>`}
                <button id="pbg-show-mosaic" class="btn"${busy || !raster2dDone ? " disabled" : ""}>Show 2D Mosaic</button>
              </div>
              ${batch2dRunning ? `<div class="small" style="margin-top:6px;">Submitting jobs: ${batch2dDone}/${batch2dTotal}</div>` : ""}
              ${raster2dFailed ? `<div class="small" style="margin-top:4px; color:#ef4444;">${raster2dFailed} tile(s) failed (2D) — re-running Generate 2D will retry them.</div>` : ""}
            </div>

            <div style="margin-top:12px; padding-top:10px; border-top:1px solid rgba(148,163,184,.22);">
              <div class="small" style="font-weight:600;">Classification Layer (per-class COGs, ASPRS colors at view time)</div>
              <div class="small" style="margin-top:4px; opacity:.75;">${clsDone}/${uploadedCount} classified${clsActive ? ` · ${clsActive} active` : ""}${mosaicInfo?.attrs?.classification?.classes?.length ? ` · classes [${mosaicInfo.attrs.classification.classes.join(", ")}]` : ""} — uses the EPSG/resolution above</div>
              <div class="row lm-add-row" style="margin-top:6px;">
                ${batchClsRunning
                  ? `<button id="pbg-stop-cls" class="btn">Stop Classification Batch</button>`
                  : `<button id="pbg-start-cls" class="btn primary"${busy || !clsTodo ? " disabled" : ""}>Generate Classification (${clsTodo} tile${clsTodo === 1 ? "" : "s"})</button>`}
                <button id="pbg-show-cls-mosaic" class="btn"${busy || !clsDone ? " disabled" : ""}>Show Classification</button>
              </div>
              ${batchClsRunning ? `<div class="small" style="margin-top:6px;">Submitting jobs: ${batchClsDone}/${batchClsTotal}</div>` : ""}
              ${clsFailed ? `<div class="small" style="margin-top:4px; color:#ef4444;">${clsFailed} tile(s) failed (classification) — re-running Generate Classification will retry them.</div>` : ""}
            </div>

            <div class="small" style="margin-top:8px; color:${busy ? "#7dd3fc" : "#cbd5e1"};">${esc(status || "Idle.")}</div>

            <div id="pbg-tile-list" style="margin-top:8px; max-height:280px; overflow:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <table style="width:100%; border-collapse:collapse; font-size:11px;">
                <thead><tr><th style="text-align:left; padding:4px 6px;">Tile</th><th style="text-align:left; padding:4px 6px;">3D (Potree)</th><th style="text-align:left; padding:4px 6px;">2D (COG)</th><th style="text-align:left; padding:4px 6px;">Class</th></tr></thead>
                <tbody>
                  ${tiles.map((t) => `
                    <tr style="border-top:1px solid rgba(148,163,184,.12);">
                      <td style="padding:4px 6px;">${esc(t.name)}</td>
                      <td style="padding:4px 6px; min-width:96px;">${statusCell(t.jobEntry, !!t.sourcePath)}</td>
                      <td style="padding:4px 6px; min-width:96px;">${statusCell(t.rasterEntry, !!t.sourcePath)}</td>
                      <td style="padding:4px 6px; min-width:96px;">${statusCell(t.clsEntry, !!t.sourcePath)}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            </div>
          ` : ""}
        </div>
      </div>
    `;
    const listEl = containerEl.querySelector("#pbg-tile-list");
    if (listEl) listEl.scrollTop = prevScrollTop;
  }

  function mount(el) {
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("change", onChange);
    render();
    loadProjects().then(() => {
      // Backfill activeClientId for state saved before the client picker existed, so an
      // already-selected project doesn't silently disappear (the project dropdown only
      // renders once a client is selected).
      if (state.activeCode && !state.activeClientId) {
        const found = projects.find((p) => p.code === state.activeCode);
        if (found) state.activeClientId = found.clientId;
      }
      if (state.activeCode) selectProject(state.activeCode, { preserveTileIndex: true });
    });
  }

  function destroy() {
    stopPolling();
    clearTimeout(persistTimer);
    clearTimeout(persistRasterTimer);
    clearTimeout(persistClsTimer);
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("change", onChange);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      activeClientId: String(next.activeClientId || ""),
      activeCode: String(next.activeCode || ""),
      selectedTileIndexKey: String(next.selectedTileIndexKey || ""),
    };
    persist();
    render();
    if (state.activeCode) selectProject(state.activeCode, { preserveTileIndex: true });
  }

  return { mount, destroy, snapshot, restore };
}
